import { Button, Dialog, DialogActions, DialogContent, Grid, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as EditIcon } from '../../../assets/PepWise2.0/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/PepWise2.0/delete-img-new.svg';
import { CustomizedDataGrid } from '../../customized_data_grid';
import { Link } from 'react-router-dom';
import PageHeader from 'components/Header/Page';
import '../../DataEstate/data-estate-dashboard.scss';
import { ResourceMappingModal } from './modalComponent';
import { fetchResourceMappingPrograms, exportTableData } from 'service/service';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import { Column } from 'react-data-grid';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SubscriptTag from 'components/Widgets/SubscriptTag';
import eventEmitter from 'SnackBarEventEmitter';
import PageWrapper from 'components/Common/PageWraper';

type ProgramProjectCellProps = {
	row: any;
	isCellSelected: boolean;
	onToggleSubRow: (programName: string, projects: any[]) => void;
};

export const ProgramProjectCell = ({
	row,
	isCellSelected,
	onToggleSubRow
}: ProgramProjectCellProps) => {
	const isNew = (updatedAt: any) => {
		const createdDate: any = new Date(updatedAt);
		const now: any = new Date();
		return (now - createdDate) / (1000 * 60 * 60 * 24) < 1;
	}

	const hasChildren = row.projects;
	const hasChildrenofChild = row.projectName;
	const prgId = row.programId;
	const prjId = row.projectId;
	const prgName = row.programName;
	const prjName = row.projectName;
	return (
		<>
			{hasChildren && row.projects.length > 0 && (
				<CellExpanderFormatter
					isCellSelected={isCellSelected}
					expanded={row.isExpanded === true}
					onCellExpand={() => {
						onToggleSubRow(row.programName, row.projects);
					}}
				/>
			)}
			{'   '}
			<Link
				to={`/finops/resourcemapping/programs/${prgId}/${encodeURIComponent(prgName)}`}
				state={row}
				style={{
					paddingLeft: hasChildren ? '5px' : '10px',
					paddingRight: '10px',
					color: '#000',
					fontWeight: 500
				}}
			>
				{hasChildren ? row.programName : ''}
			</Link>
			<Link
				to={`/finops/resourcemapping/projects/${prgId}/${prjId}/${encodeURIComponent(prgName)}/${encodeURIComponent(prjName)}`}
				state={row}
				style={{
					paddingLeft: hasChildrenofChild ? '5px' : '10px',
					color: '#000',
					fontWeight: 400
				}}
			>
				{row.projectName}
			</Link>
			{isNew(row?.updatedAt) && <SubscriptTag label="New" />}
		</>
	);
};

const programProjectFormatter = (
	toggleSubRow: (programName: string, projects: any[]) => void
) => {
	return ({ row, isCellSelected }: any) => (
		<ProgramProjectCell
			row={row}
			isCellSelected={isCellSelected}
			onToggleSubRow={toggleSubRow}
		/>
	);
};

type DataCellProps = {
	row: any;
	programField: string;
	projectField: string;
};

export const DataCell = ({ row, programField, projectField }: DataCellProps) => {
	const hasChildren = row.projects;
	const hasChildrenofChild = row.projectName;

	return (
		<>
			{'   '}
			<Link
				to=''
				state={row}
				style={{
					paddingLeft: hasChildren ? '5px' : '10px',
					paddingRight: '10px',
					color: '#000',
					fontWeight: 500
				}}
			>
				{row[programField]}
			</Link>
			<Link
				to=''
				state={row}
				style={{
					paddingLeft: hasChildrenofChild ? '5px' : '10px',
					color: '#000',
					fontWeight: 400
				}}
			>
				{row[projectField]}
			</Link>
		</>
	);
};

const ownerFormatter = () => {
	return ({ row }: any) => (
		<DataCell
			row={row}
			programField="programOwner"
			projectField="projectOwner"
		/>
	);
};

const descriptionFormatter = () => {
	return ({ row }: any) => (
		<DataCell
			row={row}
			programField="programDescription"
			projectField="projectDescription"
		/>
	);
};

type ActionCellProps = {
	row: any;
	onAction: (type: string, event: any, props: any, hasChildren: boolean) => void;
};

export const ActionCell = ({ row, onAction }: ActionCellProps) => {
	const hasChildren = row.projects;
	return (
		<Stack direction="row" alignItems="center" height="100%">
			<EditIcon title="Edit" style={{ cursor: 'pointer' }} onClick={(e) => onAction('EDIT', e, row, hasChildren)} />
			<DeleteIcon title="Delete" style={{ cursor: 'pointer' }} onClick={(e) => onAction('DELETE', e, row, hasChildren)} />
		</Stack>
	);
};

const actionFormatter = (handleAction: any) => {
	return ({ row }: any) => (
		<ActionCell
			row={row}
			onAction={handleAction}
		/>
	);
};

const Programs = () => {
	const programExpandList = useRef<Row[]>([]);
	const [noRefresh, setNoRefresh] = useState(false);
	const [programList, setProgramList] = useState<any[]>([]);
	const [openModal, setOpenModal] = useState<any>({ isOpen: false });
	const [loading, setLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({ currentPage: 0, totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [errorMessage, setErrorMessage] = useState('');
	const [searchText, setSearchText] = useState('');
	const defaultColumnNames = ['Program & Project', 'Owner', 'Description', 'Actions'];

	interface Row {
		id: string;
		programId: any;
		programName: string;
		programDescription: string;
		programOwner: string;
		projectId: any;
		projects: [];
		createdAt: string;
		updatedAt: string;
		projectName: any,
		projectDescription: string;
		projectOwner: string;
		isExpanded?: boolean;
		isExpandedChild?: boolean;
		children: Row[];
		ischildren?: boolean;
		selected: boolean;
	}

	let dynamicWidth: any;
	useEffect(() => {
		const handleResize = () => {
			const screenWidth = window.innerWidth;
			dynamicWidth = (screenWidth / 3) - 100;
		};

		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	function getColumns(): Array<Column<Row>> {
		return [
			{
				key: 'programName',
				name: 'Program & Project',
				width: dynamicWidth,
				formatter: programProjectFormatter(toggleSubRow),
			},
			{
				key: 'programOwner',
				name: 'Owner',
				width: dynamicWidth,
				formatter: ownerFormatter(),

			},
			{
				key: 'programDescription',
				name: 'Description',
				width: dynamicWidth,
				formatter: descriptionFormatter()
			},
			{
				key: 'actions',
				name: 'Actions',
				width: 100,
				resizable: false,
				formatter: actionFormatter(handleAction)
			}
		];
	};

	const handleSorting = (e: any) => {
		programListFunc(page, pageSize, e, searchText);
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		programListFunc(value, pageSize, sortingInfo, searchText);
	};

	const handlepageSizeChange = (event: any) => {
		programListFunc(page, event.target.value, sortingInfo, searchText);
	};

	const handleAction = (action: any, e: any, props: any, hasChildren: any) => {
		e.preventDefault();
		e.stopPropagation();
		setOpenModal({
			isOpen: true,
			type: (hasChildren ? 'Program' : 'Project'),
			action,
			data: props
		});
	};

	const handleEditClose = (isCanceled: boolean = false) => {
		setOpenModal({
			isOpen: false
		});
		setNoRefresh(isCanceled);
	};

	const handleAddProgramClick = useCallback(() => {
		setOpenModal({
			isOpen: true,
			type: 'Program',
			action: 'ADD',
			data: ''
		});
	}, []);

	async function programListFunc(pageNumber: any, size: any, sortingInfo: any, searchText?: any) {
		try {
			setLoading(true);
			if (pageSize !== size) {
				pageNumber = 1;
			}
			const res = await fetchResourceMappingPrograms(pageNumber.toString(), size.toString(), sortingInfo, searchText);
			if (res.success) {
				setLoading(false);
				setProgramList(res.data.programs);
				programExpandList.current = res.data.programs;
				setPaginationData(res.data.page);
				setPage(pageNumber);
				setPageSize(size);
				setSortingInfo(sortingInfo);
			}
		} catch (error) {
			console.error(error);
			if (typeof error === "object" && error !== null && "response" in error) {
				const err = error as { response: { status: number } };
				if (err.response.status === 503)
					eventEmitter.emit("showSnackbar", "Service temporarily unavailable. Please try again later.", "info");
				else if (err.response.status === 504)
					eventEmitter.emit("showSnackbar", "Gateway Timeout", "info");
			}
		}
	}

	function toggleSubRow(name: string, resourceTypes: any) {
		const rows = programExpandList.current;
		const rowIndex = rows.findIndex((r: any) => r.programName === name);
		const row = rows[rowIndex];
		const newRows = [...rows];
		let children: Row[] = [];
		let childRows: Row[] = [];
		if (!row.isExpanded) {
			if (resourceTypes.length > 0) {
				childRows = resourceTypes;
			}
			children = childRows.map((x: any) => {
				return { ...x, isChildren: true };
			});
			newRows.splice(Number(rowIndex) + 1, 0, ...children);
			newRows[rowIndex] = { ...row, isExpanded: true, children };
			localStorage.setItem('boxRows', JSON.stringify(newRows));
			row.isExpanded = true;
		}
		else {
			children = row.children;
			newRows.splice(Number(rowIndex) + 1, children?.length);
			newRows[rowIndex] = { ...row, isExpanded: false };
			localStorage.setItem('boxRows', JSON.stringify(newRows));
			row.isExpanded = false;
		}
		programExpandList.current = newRows;
		setProgramList([...newRows]);
	}

	const handleSearchData = async (serachText: string) => {
		try {
			await programListFunc(page, pageSize, sortingInfo, serachText);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		const handleProgramsSearch = (event: any) => {
			const text = event.detail?.searchText || '';
			setSearchText(text);
			setPage(1);
			programListFunc(1, pageSize, sortingInfo, text);
		};

		globalThis.addEventListener('programsSearch', handleProgramsSearch);
		return () => {
			globalThis.removeEventListener('programsSearch', handleProgramsSearch);
		};
	}, [pageSize, sortingInfo]);

	useEffect(() => {
		const activeId = document.getElementById('root');
		if (window.location.pathname.includes('/finops/resourcemapping/programs')) {
			if (activeId) {
				document.head.classList.add('theme-bg-blue');
				document.body.classList.add('theme-bg-blue');
			}
		}
		if (!openModal.isOpen && !noRefresh) {
			setSortingInfo({ sortBy: '', sortDir: '' });
			sortingInfo.sortBy = sortingInfo.sortDir = "";
			programListFunc(page, pageSize, sortingInfo, searchText);
		}
		setNoRefresh(false);
	}, [openModal]);


	// export
	const columnMap: { [key: string]: string } = {
		"Program & Project": "Program_Project",
		"Owner": "Owner",
		"Description": "Description"
	};
	const exportProgramList = async (prefColumns?: any) => {
		let tableName = "finopsProgramTable";
		let formatType = "EXCEL";
		// Map display names to internal keys
		const exportColumns = prefColumns.map((prefCol: string) => columnMap[prefCol])
			.filter((col: string) => col !== null && col !== undefined);

		await exportTableData({ tableName, formatType, exportColumns, sortingInfo });
	};

	return (
		<PageWrapper
			sx={{
				mt: { xs: '80px', sm: '50px', md: '70px' },
				pt: { lg: 2 },
				zIndex: 90
			}}>
			<Stack p={2}>
				<Dialog open={openModal.isOpen} onClose={() => handleEditClose(true)} fullWidth={true} maxWidth="md">
					<ResourceMappingModal
						handleEditClose={handleEditClose}
						programData={openModal}
						errorMessage={errorMessage}
						setErrorMessage={setErrorMessage}
					/>
				</Dialog>
				<Dialog open={!!errorMessage} maxWidth="sm">
					<DialogContent
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px'
						}}
					>
						<ErrorOutlineIcon sx={{ color: 'red', fontSize: '22px' }} />
						<Typography>{errorMessage}</Typography>
					</DialogContent>
					<DialogActions
						sx={{
							pr: '1rem',
							pb: '1rem'
						}}
					>
						<Button onClick={() => setErrorMessage('')} variant="pepwiseSecondary">
							OK
						</Button>
					</DialogActions>
				</Dialog>
				<Grid item xs={12}>
					<CustomizedDataGrid
						childrenContainerProps={{ width: '100%' }}
						tableColumns={getColumns()}
						tableData={programList}
						tableType="programs-list"
						defaultColumnNames={defaultColumnNames}
						onSorting={handleSorting}
						currSorting={sortingInfo}
						page={page}
						pageSize={pageSize}
						paginationData={paginationData}
						listLength={programList.length}
						showExport={true}
						fileName={`programs_Page_${paginationData.currentPage}_of_${paginationData.totalPages}.xlsx`}
						handlepageSizeChange={handlepageSizeChange}
						handlePageChange={handlePageChange}
						isDateRangeFilter={true}
						isFetching={loading}
						isDisabled={false}
						isShowMoreButton={true}
						exportTableData={(prefColumns?: string[]) => exportProgramList(prefColumns)}
						isFinopsProgramsList={true}
						childRowClass={true}
						isSortIcon={true}
					>
						<PageHeader title="Programs" headerSx={{ top: '86px !important', width: '100%', flexDirection: 'row !important' }}>
							<Stack direction="row" gap={1} alignItems="left">
								<Button variant="pepwiseSecondary" sx={{ px: 4, py: 1.2, m: 0 }} onClick={handleAddProgramClick}>
									CREATE PROGRAM
								</Button>
							</Stack>
						</PageHeader>
					</CustomizedDataGrid>
				</Grid>
			</Stack>
		</PageWrapper>
	);
};

export default Programs;
